import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const ROLE_COLORS = {
  SUPER_ADMIN: "bg-brand-orange/15 text-brand-orange border-brand-orange/40",
  EXEC: "bg-navy-600/10 text-navy-600 border-navy-600/40",
  SITE_MANAGER: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

const EMPTY_CREATE = {
  email: "",
  password: "",
  name: "",
  role: "SITE_MANAGER",
  companyAccess: [],
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null); // { ...EMPTY_CREATE } or existing user + maybe password
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/companies")])
      .then(([u, c]) => {
        setUsers(u.data);
        setCompanies(c.data);
      })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleAccess = (cid) => {
    setEditing((e) => {
      const has = e.companyAccess?.includes(cid);
      const next = has
        ? e.companyAccess.filter((x) => x !== cid)
        : [...(e.companyAccess || []), cid];
      return { ...e, companyAccess: next };
    });
  };

  const save = async () => {
    try {
      if (editing.user_id) {
        const body = {
          name: editing.name,
          role: editing.role,
          companyAccess: editing.companyAccess,
        };
        if (editing.password) body.password = editing.password;
        await api.patch(`/users/${editing.user_id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", editing);
        toast.success("User created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Users & Roles"
        subtitle="Manage access, company scoping, and roles"
        right={
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY_CREATE })}
            data-testid="users-add-button"
            className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add user
          </button>
        }
      />

      <div className="p-6 animate-fade-in" data-testid="users-page">
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 text-left">User</th>
                <th className="px-5 py-3 text-left">Role</th>
                <th className="px-5 py-3 text-left">Provider</th>
                <th className="px-5 py-3 text-left">Companies</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading &&
                users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    data-testid={`user-row-${u.user_id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-navy-600 text-white">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="h-full w-full rounded-sm object-cover" />
                          ) : (
                            <span className="font-semibold">
                              {u.name?.[0]?.toUpperCase() || "?"}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-navy-600">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                          ROLE_COLORS[u.role] || "border-slate-200"
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs uppercase tracking-widest text-slate-500">
                      {u.auth_provider}
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {u.companyAccess?.length || 0} / {companies.length}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({ ...u, password: "" })
                          }
                          data-testid={`user-edit-${u.user_id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(u)}
                          data-testid={`user-delete-${u.user_id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-600/70 p-4 backdrop-blur-sm"
          data-testid="users-modal"
        >
          <div className="w-full max-w-2xl rounded-md border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="font-display text-base font-semibold text-navy-600">
                {editing.user_id ? "Edit user" : "New user"}
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-sm p-1 text-slate-400 hover:text-navy-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
              <Field
                label="Name"
                value={editing.name}
                onChange={(v) => setEditing({ ...editing, name: v })}
                testId="user-input-name"
              />
              <Field
                label="Email"
                value={editing.email}
                onChange={(v) => setEditing({ ...editing, email: v })}
                disabled={!!editing.user_id}
                testId="user-input-email"
              />
              <Field
                label={editing.user_id ? "Reset password (optional)" : "Password"}
                type="password"
                value={editing.password || ""}
                onChange={(v) => setEditing({ ...editing, password: v })}
                testId="user-input-password"
              />
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Role
                </label>
                <select
                  value={editing.role}
                  onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                  data-testid="user-input-role"
                  className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                >
                  <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                  <option value="EXEC">EXEC</option>
                  <option value="SITE_MANAGER">SITE_MANAGER</option>
                </select>
              </div>
            </div>

            <div className="border-t border-slate-100 px-5 py-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                Company access ({editing.companyAccess?.length || 0} / {companies.length})
              </div>
              <div className="grid max-h-40 grid-cols-2 gap-1 overflow-y-auto sm:grid-cols-3">
                {companies.map((c) => {
                  const on = editing.companyAccess?.includes(c.company_id);
                  return (
                    <button
                      key={c.company_id}
                      type="button"
                      onClick={() => toggleAccess(c.company_id)}
                      className={`truncate rounded-sm border px-2 py-1.5 text-left text-xs transition-colors ${
                        on
                          ? "border-brand-orange bg-brand-orange/10 text-brand-orange"
                          : "border-slate-200 text-slate-500 hover:border-navy-600"
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-9 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="user-save-button"
                className="h-9 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value, onChange, disabled, type = "text", testId }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testId}
        className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  );
}
